import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { AuthMethods, User } from '@klingerator/shared';
import { api, backendBaseUrl, setAuthToken, setUnauthorizedHandler } from '@/services/api';

const TOKEN_KEY = 'kl.token';

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(localStorage.getItem(TOKEN_KEY));
  const user = ref<User | null>(null);
  const initialized = ref(false);
  const methods = ref<AuthMethods | null>(null);

  const isAuthenticated = computed(() => !!token.value && !!user.value);
  const isAdmin = computed(() => user.value?.role === 'admin');
  /** Goedkeurder óf admin: mag inzendingen beoordelen en naar Unit4 schrijven. */
  const isApprover = computed(() => user.value?.role === 'approver' || user.value?.role === 'admin');
  const isActive = computed(() => !!user.value && user.value.role !== 'pending' && user.value.is_active);

  setAuthToken(token.value);
  setUnauthorizedHandler(() => {
    if (token.value) logout(false);
  });

  async function fetchMe(): Promise<void> {
    if (!token.value) {
      user.value = null;
      return;
    }
    try {
      const { data } = await api.get<{ user: User }>('/auth/me', { silent: true, noToast: true });
      user.value = data.user;
    } catch {
      user.value = null;
      token.value = null;
      localStorage.removeItem(TOKEN_KEY);
      setAuthToken(null);
    }
  }

  async function initialize(): Promise<void> {
    if (initialized.value) return;
    await fetchMe();
    initialized.value = true;
  }

  async function loadMethods(): Promise<void> {
    if (methods.value) return;
    try {
      const { data } = await api.get<AuthMethods>('/auth/methods', { silent: true, noToast: true });
      methods.value = {
        azure: !!data.azure,
        google: !!data.google,
        local: !!data.local,
        registration: !!data.registration,
        dev: !!data.dev,
        environment: data.environment ?? '',
      };
    } catch {
      methods.value = {
        azure: true,
        google: false,
        local: true,
        registration: false,
        dev: false,
        environment: '',
      };
    }
  }

  /** Full-page redirect naar de backend-origin (SSO-cookies moeten op het backend-domein staan). */
  function loginWithProvider(provider: 'azure' | 'google', redirect = '/') {
    const url = `${backendBaseUrl}/api/auth/${provider}?redirect=${encodeURIComponent(redirect)}`;
    window.location.href = url;
  }
  const loginWithAzure = (redirect = '/') => loginWithProvider('azure', redirect);
  const loginWithGoogle = (redirect = '/') => loginWithProvider('google', redirect);

  /** E-mail + wachtwoord. */
  async function login(email: string, password: string): Promise<void> {
    const { data } = await api.post<{ token: string; user: User }>(
      '/auth/login',
      { email, password },
      { noToast: true },
    );
    await handleToken(data.token);
  }

  /** Registreren met e-mail + wachtwoord (nieuw account wacht op goedkeuring). */
  async function register(email: string, password: string, name: string): Promise<void> {
    const { data } = await api.post<{ token: string; user: User }>(
      '/auth/register',
      { email, password, name },
      { noToast: true },
    );
    await handleToken(data.token);
  }

  /** Alleen in development: inloggen op e-mail zonder wachtwoord. */
  async function loginDev(email: string): Promise<void> {
    const { data } = await api.post<{ token: string; user: User }>(
      '/auth/dev-login',
      { email },
      { noToast: true },
    );
    await handleToken(data.token);
  }

  async function handleToken(newToken: string): Promise<void> {
    token.value = newToken;
    localStorage.setItem(TOKEN_KEY, newToken);
    setAuthToken(newToken);
    await fetchMe();
  }

  function logout(redirect = true) {
    token.value = null;
    user.value = null;
    localStorage.removeItem(TOKEN_KEY);
    setAuthToken(null);
    if (redirect) window.location.href = '/login';
  }

  return {
    token,
    user,
    initialized,
    methods,
    isAuthenticated,
    isAdmin,
    isApprover,
    isActive,
    initialize,
    fetchMe,
    loadMethods,
    loginWithAzure,
    loginWithGoogle,
    login,
    register,
    loginDev,
    handleToken,
    logout,
  };
});
